/**
 * Общая карточка контура единой карты (agroai-6al.1).
 *
 * Нажатие по контуру на ЛЮБОЙ карте открывает эту карточку, а не уводит со
 * страницы: номер, площадь, культура, хозяйство и землепользователь ПО ИМЕНИ
 * со ссылкой, значение текущего слоя и переходы. Страница может добавить свой
 * раздел (`extra`), но не собирает карточку заново.
 *
 * Две подачи одного содержимого: `side` — панель внутри кадра (станция карты и
 * развёрнутый кадр), `drawer` — боковая шторка дизайн-системы (`DetailDrawer`)
 * для небольших кадров внутри страниц.
 */

import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, X } from 'lucide-react';
import { DetailDrawer } from '../ui/DetailDrawer';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Icon } from '../ui/Icon';
import { haUnit } from '../lib/format';
import { GEOMETRY_PHRASE } from '../model/geometryLabel';
import type { ContourMeta } from '../data/types';
import type { LegendEntry } from './paint';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  area: {
    id: 'map.contourCard.area',
    defaultMessage: 'Площадь',
    description: 'Карточка контура на карте (боковая панель): название строки «Площадь»; коротко',
  },
  areaNone: {
    id: 'map.contourCard.areaNone',
    defaultMessage: 'не названа',
    description: 'Карточка контура на карте (боковая панель): значение площади, когда источник её не назвал (не ноль); коротко',
  },
  crop: {
    id: 'map.contourCard.crop',
    defaultMessage: 'Культура 2026',
    description: 'Карточка контура на карте (боковая панель): название строки «Культура 2026»; коротко',
  },
  cropNone: {
    id: 'map.contourCard.cropNone',
    defaultMessage: 'не размещено',
    description: 'Карточка контура на карте (боковая панель): значение культуры, когда на контуре ничего не размещено; коротко',
  },
  farm: {
    id: 'map.contourCard.farm',
    defaultMessage: 'Хозяйство',
    description: 'Карточка контура на карте (боковая панель): название строки «Хозяйство»; коротко',
  },
  farmNo: {
    id: 'map.contourCard.farmNo',
    defaultMessage: 'Хозяйство {id}',
    description: 'Карточка контура на карте (боковая панель): название хозяйства, когда имя неизвестно; {id} — номер хозяйства',
  },
  farmNone: {
    id: 'map.contourCard.farmNone',
    defaultMessage: 'не сопоставлено',
    description: 'Карточка контура на карте (боковая панель): значение хозяйства, когда контур не сопоставлен ни с каким хозяйством; коротко',
  },
  landUsers: {
    id: 'map.contourCard.landUsers',
    defaultMessage: 'Землепользователи',
    description: 'Карточка контура на карте (боковая панель): название строки с остальными землепользователями; коротко',
  },
  moreUsers: {
    id: 'map.contourCard.moreUsers',
    defaultMessage: 'ещё {count, plural, one {# землепользователь} few {# землепользователя} many {# землепользователей} other {# землепользователя}} — список в полной карточке',
    description: 'Карточка контура на карте (боковая панель): сколько ещё землепользователей у контура помимо названного; {count} — число',
  },
  massif: {
    id: 'map.contourCard.massif',
    defaultMessage: 'Массив',
    description: 'Карточка контура на карте (боковая панель): название строки «Массив»; коротко',
  },
  massifNo: {
    id: 'map.contourCard.massifNo',
    defaultMessage: 'Массив {id}',
    description: 'Карточка контура на карте (боковая панель): название массива, когда имя неизвестно; {id} — номер массива',
  },
  noLayerValue: {
    id: 'map.contourCard.noLayerValue',
    defaultMessage: 'значение слоя для контура не передано',
    description: 'Карточка контура на карте (боковая панель): под названием слоя: значение выбранного слоя для этого контура не пришло',
  },
  fullCard: {
    id: 'map.contourCard.fullCard',
    defaultMessage: 'Полная карточка контура',
    description: 'Карточка контура на карте (боковая панель): кнопка-ссылка на полную страницу контура',
  },
  farmCard: {
    id: 'map.contourCard.farmCard',
    defaultMessage: 'Карточка хозяйства',
    description: 'Карточка контура на карте (боковая панель): ссылка на страницу хозяйства',
  },
  geometry: {
    id: 'map.contourCard.geometry',
    defaultMessage: 'Геометрия: {phrase}.',
    description: 'Карточка контура на карте (боковая панель): сноска про геометрию контура; {phrase} — «принятая привязка», «нет привязки» и т. п. (строчная фраза)',
  },
  noRegistryRow: {
    id: 'map.contourCard.noRegistryRow',
    defaultMessage: 'У полигона нет строки в реестре контуров среза: атрибуты неизвестны.',
    description: 'Карточка контура на карте (боковая панель): сноска, когда полигон есть на карте, а строки в реестре нет',
  },
  title: {
    id: 'map.contourCard.title',
    defaultMessage: 'Контур № {no}',
    description: 'Карточка контура на карте (боковая панель): заголовок карточки; {no} — номер контура',
  },
  close: {
    id: 'map.contourCard.close',
    defaultMessage: 'Закрыть карточку',
    description: 'Карточка контура на карте (боковая панель): aria-label кнопки-крестика',
  },
});


/** Всё, что карточке нужно знать о контуре: собирает кадр карты. */
export interface ContourCardData {
  id: string;
  no: string;
  /** Строка реестра среза; `null` — полигон без строки (атрибуты неизвестны). */
  meta: ContourMeta | null;
  /** Площадь, га; `null` — источник площадь не назвал (не ноль). */
  areaHa: number | null;
  cropName: string | null;
  farmId: string | null;
  farmName: string | null;
  massifId: string | null;
  massifName: string | null;
  /** Землепользователь по слою канона, если строки хозяйства нет. */
  landUser: string | null;
  /** Сколько землепользователей у контура; > 1 — земля делится. */
  landUsersCount: number | null;
  /** Значение текущего слоя: позиция легенды и заголовок слоя. */
  layerTitle: string;
  layerEntry: LegendEntry | null;
  /** Вкладка полной карточки контура по слою (`?tab=`); нет — вкладка по умолчанию. */
  contourTab?: string;
}

export interface ContourCardProps {
  data: ContourCardData;
  variant: 'side' | 'drawer';
  onClose: () => void;
  /** Query-строка контекста анализа для переходов (`?as_of=…`). */
  linkSearch?: string;
  /** Раздел страницы под общей частью. */
  extra?: ReactNode;
}

function swatch(entry: LegendEntry): CSSProperties {
  return {
    background: entry.color === 'transparent' ? 'transparent' : entry.color,
    borderColor: entry.outline ?? 'transparent',
    borderStyle: entry.dashed ? 'dashed' : entry.dotted ? 'dotted' : 'solid',
  };
}

function Body({ data, onClose, linkSearch = '', extra }: Omit<ContourCardProps, 'variant'>) {
  // Массив — только фильтр списка контуров (решение №85), общий срез сохраняем.
  const massifParams = new URLSearchParams(linkSearch.replace(/^\?/, ''));
  if (data.massifId) massifParams.set('massif', String(data.massifId));
  const massifHref = `/contours?${massifParams.toString()}`;
  const farmHref = data.farmId ? `/farms/${data.farmId}${linkSearch}` : null;
  const t = useT();
  const owner = data.farmName ?? data.landUser;
  const others = data.landUsersCount && data.landUsersCount > 1 ? data.landUsersCount - 1 : 0;

  return (
    <div className="contourcard__body">
      <ul className="contourcard__facts">
        <li className="contourcard__row">
          <span className="contourcard__key">{t(m.area)}</span>
          <span className="contourcard__val num">{data.areaHa === null ? t(m.areaNone) : haUnit(data.areaHa)}</span>
        </li>
        <li className="contourcard__row">
          <span className="contourcard__key">{t(m.crop)}</span>
          <span className="contourcard__val">{data.cropName ?? t(m.cropNone)}</span>
        </li>
        <li className="contourcard__row">
          <span className="contourcard__key">{t(m.farm)}</span>
          <span className="contourcard__val">
            {owner && farmHref ? (
              <Link className="contourcard__name" to={farmHref} onClick={onClose}>
                {owner}
              </Link>
            ) : (
              (owner ?? (data.farmId ? t(m.farmNo, { id: data.farmId }) : t(m.farmNone)))
            )}
          </span>
        </li>
        {others > 0 ? (
          <li className="contourcard__row">
            <span className="contourcard__key">{t(m.landUsers)}</span>
            <span className="contourcard__val">
              {t(m.moreUsers, { count: others })}
            </span>
          </li>
        ) : null}
        {data.massifId ? (
          <li className="contourcard__row">
            <span className="contourcard__key">{t(m.massif)}</span>
            <span className="contourcard__val">
              <Link to={massifHref} onClick={onClose}>
                {data.massifName ?? t(m.massifNo, { id: data.massifId })}
              </Link>
            </span>
          </li>
        ) : null}
      </ul>

      <div className="contourcard__layer">
        <span className="contourcard__layer-title">{data.layerTitle}</span>
        {data.layerEntry ? (
          <span className="contourcard__layer-val">
            <span className="contourcard__swatch" style={swatch(data.layerEntry)} aria-hidden="true" />
            <span>
              <strong>{data.layerEntry.label}</strong>
              {data.layerEntry.note ? <span className="contourcard__note">{data.layerEntry.note}</span> : null}
            </span>
          </span>
        ) : (
          <span className="contourcard__note">{t(m.noLayerValue)}</span>
        )}
      </div>

      {extra ? <div className="contourcard__extra">{extra}</div> : null}

      <div className="contourcard__links">
        <Link className="astra-button" to={`/contours/${data.id}${withTab(linkSearch, data.contourTab)}`} onClick={onClose}>
          {t(m.fullCard)} <Icon icon={ArrowUpRight} size={16} />
        </Link>
        {farmHref ? (
          <Link className="astra-link" to={farmHref} onClick={onClose}>
            {t(m.farmCard)}
          </Link>
        ) : null}
      </div>

      {data.meta ? (
        <p className="contourcard__note">
          {t(m.geometry, { phrase: GEOMETRY_PHRASE[data.meta.geometry] })}{' '}
          <EvidenceMark kind={data.meta.geometry === 'candidate' ? 'candidate' : 'fact'} />
        </p>
      ) : (
        <p className="contourcard__note">
          {t(m.noRegistryRow)}
        </p>
      )}
    </div>
  );
}

/** Хвост адреса с вкладкой карточки контура. */
function withTab(search: string, tab: string | undefined): string {
  if (!tab) return search;
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  params.set('tab', tab);
  return `?${params.toString()}`;
}

export function ContourCard(props: ContourCardProps) {
  const t = useT();
  const title = t(m.title, { no: props.data.no });
  if (props.variant === 'drawer') {
    return (
      <DetailDrawer title={title} onClose={props.onClose}>
        <Body {...props} />
      </DetailDrawer>
    );
  }
  return (
    <aside className="contourcard" aria-label={title}>
      <header className="contourcard__head">
        <h3 className="contourcard__title">{title}</h3>
        <button type="button" className="contourcard__close focus-ring" aria-label={t(m.close)} onClick={props.onClose}>
          <Icon icon={X} size={18} />
        </button>
      </header>
      <Body {...props} />
    </aside>
  );
}
